/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";

import { IModelOutputState } from "../../shared/model/IModelOutputState";
import { IModelOutputBaseConfig, IModelOutputResultLabel } from "../../shared/model/ModelOutput";
import { ResultItem } from "../ResultItem";
import { ModelHandler } from "../../shared/model/ModelHandler";

/**
 * Result item component props
 */
interface IOutputResultItemProps {
	state: IModelOutputState<any, IModelOutputBaseConfig>;
	handler: ModelHandler;
	item: IModelOutputResultLabel<any>;
}

/**
 * Result item component
 */
function OutputResultItem(props: IOutputResultItemProps) {

	const prefix = useMemo(
		() => <ReactMarkdown source={props.item.prefix} disallowedTypes={[ "paragraph", "blockquote" ]} unwrapDisallowed={true} />,
		[ props.item ]
	);

	const content = useMemo(
		() => <ReactMarkdown source={props.item.content} disallowedTypes={[ "paragraph" ]} unwrapDisallowed={true} />,
		[ props.item ]
	);

	return <ResultItem large={props.item.large} highlight={props.item.condition(props.state, props.handler)}>
		<dt>{prefix}</dt>
		<dd>{content}</dd>
	</ResultItem>;

}

/**
 * Component props
 */
export interface IOutputResultBlockProps {
	state: IModelOutputState<any, IModelOutputBaseConfig>;
	handler: ModelHandler;
}

/**
 * Output result block component
 *
 * @param props Component props
 */
export function OutputResultBlock(props: IOutputResultBlockProps) {

	const description = useMemo(
		() => props.state.config.description ? <ReactMarkdown source={props.state.config.description} /> : null,
		[ props.state.config.description ]
	);

	const resultLabels = props.state.config.resultLabels;

	return <>
		{description}
		{ resultLabels ?
			resultLabels.map((item, index) => {
				return <OutputResultItem key={index} item={item} handler={props.handler} state={props.state} />;
			})
		: null }
	</>;

}
